import * as THREE from 'three';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import type { Character } from '@/schemas';
import { logger } from './logger';

export interface AnimatedModelData {
  model: THREE.Object3D;
  animations: {
    running: THREE.AnimationClip;
    jumping: THREE.AnimationClip;
  };
}

export class AnimatedModelLoader {
  private loader: FBXLoader;

  constructor() {
    this.loader = new FBXLoader();
  }

  /**
   * キャラクターのアニメーション付きモデルを読み込む
   */
  public async loadAnimations(character: Character): Promise<AnimatedModelData> {
    const paths = character.animatedModelPaths;

    try {
      // 1. Runファイルからモデルとアニメーションを取得
      logger.log(`Loading Run model for ${character.name}: ${paths.running}`);
      const runData = await this.loadFBX(paths.running);
      const model = runData.model;
      const runClip = runData.clips[0];

      if (!runClip) {
        throw new Error(`No animation clip found in Run file: ${paths.running}`);
      }

      // 2. Jumpファイルからアニメーションのみ取得
      logger.log(`Loading Jump animation for ${character.name}: ${paths.jumping}`);
      const jumpData = await this.loadFBX(paths.jumping);
      const jumpClip = jumpData.clips[0];

      if (!jumpClip) {
        throw new Error(`No animation clip found in Jump file: ${paths.jumping}`);
      }


      // 4. モデルのスケールを調整
      this.scaleModel(model);

      // 5. モデルの位置を調整：X/Z方向は中心を、Y方向は底面を原点に
      const box = new THREE.Box3().setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());
      const min = box.min;

      // X/Z方向：中心を原点に、Y方向：底面を原点に
      model.position.x -= center.x;
      model.position.y -= min.y;
      model.position.z -= center.z;


      // 6. モデルをGroupでラップして、グループを回転
      // こうすることでバウンディングボックスが正しく計算される
      const group = new THREE.Group();
      group.add(model);
      group.rotation.y = Math.PI / 2; // グループを回転

      logger.log(`Successfully loaded animated model for ${character.name}`);

      return {
        model: group,
        animations: {
          running: runClip,
          jumping: jumpClip,
        },
      };
    } catch (error) {
      logger.error(`Failed to load animations for ${character.name}:`, error);
      throw error;
    }
  }

  /**
   * FBXファイルを読み込む
   */
  private loadFBX(path: string): Promise<{ model: THREE.Object3D; clips: THREE.AnimationClip[] }> {
    return new Promise((resolve, reject) => {
      this.loader.load(
        path,
        (fbx) => {
          resolve({
            model: fbx,
            clips: fbx.animations,
          });
        },
        undefined, // 進捗コールバック（不要）
        (error) => {
          logger.error(`Error loading FBX file ${path}:`, error);
          reject(error);
        }
      );
    });
  }

  /**
   * モデルのスケールと向きを調整
   */
  private scaleModel(model: THREE.Object3D): void {
    // バウンディングボックスを計算
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const maxDimension = Math.max(size.x, size.y, size.z);

    // 目標サイズ（現在のゲーム環境に合わせる）
    const targetSize = 2;
    const scale = targetSize / maxDimension;

    model.scale.setScalar(scale);
  }
}
